/**
 * Stable export capability identifiers persisted in organisation settings.
 */
export const MEET_EXPORT_CAPABILITIES = [
  "OPENPOWERLIFTING",
  "FFFORCE_MEET_SHEET",
  "FFFORCE_MACRO",
] as const;
/** Export formats that an organisation may expose. */
export type MeetExportCapability = (typeof MEET_EXPORT_CAPABILITIES)[number];
/**
 * Stable import capability identifiers persisted in organisation settings.
 * FFFORCE_MEET_SHEET = legacy one-file-per-group federation sheet.
 * GENERIC_MEET_SHEET = multi-sheet workbook (Competition + one group per tab).
 */
export const MEET_IMPORT_CAPABILITIES = [
  "FFFORCE_MEET_SHEET",
  "GENERIC_MEET_SHEET",
] as const;
/** Import formats that an organisation may expose. */
export type MeetImportCapability = (typeof MEET_IMPORT_CAPABILITIES)[number];
/** Configurable domains that may use another organisation as their source. */
export type FederationSourceDomain =
  | "discipline"
  | "ageCategory"
  | "weightCategory"
  | "level"
  | "record"
  | "referee";
/** Public identity safe to expose alongside public meets. */
export type OrganisationPublicIdentity = {
  id: string;
  code: string | null;
  name: string;
  countryCode: string | null;
  recordLabel: string | null;
  recordColor: string | null;
  recordBadgeUrl: string | null;
};
/** Explicit source selected for every federation-owned data domain. */
export type FederationSources = Record<FederationSourceDomain, string>;
/** Effective organisation settings consumed by web and Electron clients. */
export type OrganisationFederationConfig = {
  organisation: OrganisationPublicIdentity;
  sources: FederationSources;
  recordOrganisationIds: string[];
  enabledExports: MeetExportCapability[];
  enabledImports: MeetImportCapability[];
  openPowerliftingFederation: string | null;
  openPowerliftingCountry: string | null;
  /** Powerscout PS_FEDERATION name for national speaker rankings (null = FFForce fallback). */
  powerscoutFederationName: string | null;
  /**
   * Label shown when no minima row matches the athlete total.
   * Null/empty → {@link DEFAULT_LEVEL_NAME_FALLBACK} ("dépt.").
   */
  defaultLevelName: string | null;
};
/** Legacy FFForce label used when an organisation has not configured a default. */
export const DEFAULT_LEVEL_NAME_FALLBACK = "dépt.";
/**
 * Resolves the display label for athletes below every minima row.
 *
 * @param value - Organisation-configured label (null/blank = legacy "dépt.").
 * @returns Non-empty level name to show in grids and exports.
 */
export function resolveDefaultLevelName(
  value: string | null | undefined,
): string {
  const trimmed = typeof value === "string" ? value.trim() : "";
  return trimmed.length > 0 ? trimmed : DEFAULT_LEVEL_NAME_FALLBACK;
}
/** Portable discipline row included in a federation snapshot. */
export type FederationDiscipline = {
  id: number;
  name: string;
  code: string;
  competitionFormat: string;
  organisationId: string;
  displayOrder: number;
};
/** Portable age category row included in a federation snapshot. */
export type FederationAgeCategory = {
  id: number;
  name: string;
  recordName: string | null;
  code: string | null;
  ageMin: number;
  ageMax: number;
  disciplineId: number;
  organisationId: string;
  isOpen: boolean;
  displayOrder: number;
};
/** Portable weight category row included in a federation snapshot. */
export type FederationWeightCategory = {
  id: number;
  name: string;
  minWeight: number;
  maxWeight: number;
  isJunior: boolean;
  sex: string;
  organisationId: string;
  displayOrder: number;
};
/** Portable level row included in a federation snapshot. */
export type FederationLevel = {
  id: number;
  minTotal: number;
  maxTotal: number;
  name: string;
  ageCategoryId: number;
  weightCategoryId: number;
  disciplineId: number;
  sheetDate: string;
  organisationId: string;
};
/** Record identity carried with each scope for cumulative comparisons. */
export type FederationRecord = {
  id: number;
  movement: string;
  weight: number;
  ageCategoryId: number;
  weightCategoryId: number;
  dateLocation: string;
  athleteName: string;
  disciplineId: number;
  sex: string;
  sheetDate: string;
  organisationId: string;
  organisation: OrganisationPublicIdentity;
};
/** Complete effective federation payload cached by Electron. */
export type FederationSnapshot = {
  config: OrganisationFederationConfig;
  /** Direct child organisations used as the Electron "ligue" list. */
  leagues: OrganisationPublicIdentity[];
  disciplines: FederationDiscipline[];
  ageCategories: FederationAgeCategory[];
  weightCategories: FederationWeightCategory[];
  levels: FederationLevel[];
  records: FederationRecord[];
  generatedAt: string;
};
/** Current version of the portable records workbook contract. */
export const FEDERATION_RECORDS_WORKBOOK_VERSION = 3;
/** Current version of the portable levels / minima workbook contract. */
export const FEDERATION_LEVELS_WORKBOOK_VERSION = 3;
/**
 * Removes unknown export values before they reach UI or authorization checks.
 *
 * @param value - JSON value loaded from persistence.
 * @returns Valid, deduplicated export capabilities.
 */
export function parseMeetExportCapabilities(
  value: unknown,
): MeetExportCapability[] {
  if (!Array.isArray(value)) return [];
  const allowed = new Set<string>(MEET_EXPORT_CAPABILITIES);
  return [
    ...new Set(
      value.filter(
        (item): item is MeetExportCapability =>
          typeof item === "string" && allowed.has(item),
      ),
    ),
  ];
}
/**
 * Removes unknown import values before they reach UI or authorization checks.
 *
 * @param value - JSON value loaded from persistence.
 * @returns Valid, deduplicated import capabilities.
 */
export function parseMeetImportCapabilities(
  value: unknown,
): MeetImportCapability[] {
  if (!Array.isArray(value)) return [];
  const allowed = new Set<string>(MEET_IMPORT_CAPABILITIES);
  return [
    ...new Set(
      value.filter(
        (item): item is MeetImportCapability =>
          typeof item === "string" && allowed.has(item),
      ),
    ),
  ];
}
/**
 * Derives default import capabilities from export flags (migration / create fallback).
 * Orgs with FFForce sheet export also get FFForce import + generic; others get generic only.
 *
 * @param enabledExports - Parsed export capabilities.
 * @returns Default import capability list.
 */
export function defaultMeetImportCapabilities(
  enabledExports: MeetExportCapability[],
): MeetImportCapability[] {
  if (enabledExports.includes("FFFORCE_MEET_SHEET")) {
    return ["FFFORCE_MEET_SHEET", "GENERIC_MEET_SHEET"];
  }
  return ["GENERIC_MEET_SHEET"];
}
/**
 * Resolves import capabilities, falling back when the column is still unset (pre-migration).
 * An explicit empty array is preserved (admin disabled all imports).
 *
 * @param enabledImports - Raw JSON from persistence (null/undefined = unset).
 * @param enabledExports - Raw export JSON used only for the unset fallback.
 * @returns Effective import capabilities.
 */
export function resolveMeetImportCapabilities(
  enabledImports: unknown,
  enabledExports: unknown,
): MeetImportCapability[] {
  if (enabledImports === null || enabledImports === undefined) {
    return defaultMeetImportCapabilities(
      parseMeetExportCapabilities(enabledExports),
    );
  }
  return parseMeetImportCapabilities(enabledImports);
}
